import ItemMeasurement from "@/components/items/model/ItemMeasurement";
import {DimensionUnit} from "@/components/properties/settings/measure-units/model/DimensionUnit";
import {WeightUnit} from "@/components/properties/settings/measure-units/model/WeightUnit";
import {VolumeUnit} from "@/components/properties/settings/measure-units/model/VolumeUnit";
import convert from "convert-units";

export class ItemsHelper {
  public static convertMeasurementsToConfiguredUnits(itemMeasurement: ItemMeasurement,
                                                     dimensionUnit: DimensionUnit,
                                                     weightUnit: WeightUnit,
                                                     volumeUnit: VolumeUnit): ItemMeasurement {
    return {
      length: this.roundToFixedDigits(this.convertDimension(itemMeasurement.length, DimensionUnit.MILLIMETERS, dimensionUnit), 5),
      width: this.roundToFixedDigits(this.convertDimension(itemMeasurement.width, DimensionUnit.MILLIMETERS, dimensionUnit), 5),
      height: this.roundToFixedDigits(this.convertDimension(itemMeasurement.height, DimensionUnit.MILLIMETERS, dimensionUnit), 5),
      weight: this.roundToFixedDigits(this.convertWeight(itemMeasurement.weight, WeightUnit.GRAMS, weightUnit), 5),
      volume: this.roundToFixedDigits(this.convertVolume(itemMeasurement.volume, VolumeUnit.CUBIC_MM, volumeUnit), 7),
    };
  }

  public static convertMeasurementsToDefaultUnits(itemMeasurement: ItemMeasurement,
                                                  dimensionUnit: DimensionUnit,
                                                  weightUnit: WeightUnit,
                                                  volumeUnit: VolumeUnit): ItemMeasurement {
    return {
      length: this.convertDimension(itemMeasurement.length, dimensionUnit, DimensionUnit.MILLIMETERS),
      width: this.convertDimension(itemMeasurement.width, dimensionUnit, DimensionUnit.MILLIMETERS),
      height: this.convertDimension(itemMeasurement.height, dimensionUnit, DimensionUnit.MILLIMETERS),
      weight: this.convertWeight(itemMeasurement.weight, weightUnit, WeightUnit.GRAMS),
      volume: this.convertVolume(itemMeasurement.volume, volumeUnit, VolumeUnit.CUBIC_MM),
    };
  }

  public static convertDimension(value: number | null, fromUnit: DimensionUnit, toUnit: DimensionUnit): number | null {
    if (value === null) {
      return null;
    }
    try {
      return convert(value).from(fromUnit).to(toUnit);
    } catch (e) {
      return null;
    }
  }

  public static convertWeight(value: number | null, fromUnit: WeightUnit, toUnit: WeightUnit): number | null {
    if (value === null) {
      return null;
    }
    try {
      return convert(value).from(fromUnit).to(toUnit);
    } catch (e) {
      return null;
    }
  }

  public static convertVolume(value: number | null, fromUnit: VolumeUnit, toUnit: VolumeUnit): number | null {
    if (value === null) {
      return 0;
    }
    if (fromUnit === toUnit) {
      return value;
    }

    const conversionTable: Record<VolumeUnit, Record<VolumeUnit, number>> = {
      [VolumeUnit.CUBIC_MM]: {
        [VolumeUnit.CUBIC_CM]: 1 / 1000,
        [VolumeUnit.CUBIC_DM]: 1 / 1_000_000,
        [VolumeUnit.CUBIC_M]: 1 / 1_000_000_000,
        [VolumeUnit.CUBIC_INCHES]: 1 / 16387.064,
        [VolumeUnit.CUBIC_FEET]: 1 / 28_316_846.592,
        [VolumeUnit.CUBIC_MM]: 1,
      },
      [VolumeUnit.CUBIC_CM]: {
        [VolumeUnit.CUBIC_MM]: 1000,
        [VolumeUnit.CUBIC_DM]: 1 / 1000,
        [VolumeUnit.CUBIC_M]: 1 / 1_000_000,
        [VolumeUnit.CUBIC_INCHES]: 1 / 16.387064,
        [VolumeUnit.CUBIC_FEET]: 1 / 28_316.846592,
        [VolumeUnit.CUBIC_CM]: 1,
      },
      [VolumeUnit.CUBIC_DM]: {
        [VolumeUnit.CUBIC_MM]: 1_000_000,
        [VolumeUnit.CUBIC_CM]: 1000,
        [VolumeUnit.CUBIC_M]: 1 / 1000,
        [VolumeUnit.CUBIC_INCHES]: 61.0237441,
        [VolumeUnit.CUBIC_FEET]: 0.0353146667,
        [VolumeUnit.CUBIC_DM]: 1,
      },
      [VolumeUnit.CUBIC_M]: {
        [VolumeUnit.CUBIC_MM]: 1_000_000_000,
        [VolumeUnit.CUBIC_CM]: 1_000_000,
        [VolumeUnit.CUBIC_DM]: 1000,
        [VolumeUnit.CUBIC_INCHES]: 61_023.7441,
        [VolumeUnit.CUBIC_FEET]: 35.3146667,
        [VolumeUnit.CUBIC_M]: 1,
      },
      [VolumeUnit.CUBIC_INCHES]: {
        [VolumeUnit.CUBIC_MM]: 16_387.064,
        [VolumeUnit.CUBIC_CM]: 16.387064,
        [VolumeUnit.CUBIC_DM]: 0.016387064,
        [VolumeUnit.CUBIC_M]: 0.000016387064,
        [VolumeUnit.CUBIC_FEET]: 1 / 1728,
        [VolumeUnit.CUBIC_INCHES]: 1,
      },
      [VolumeUnit.CUBIC_FEET]: {
        [VolumeUnit.CUBIC_MM]: 28_316_846.592,
        [VolumeUnit.CUBIC_CM]: 28_316.846592,
        [VolumeUnit.CUBIC_DM]: 28.316846592,
        [VolumeUnit.CUBIC_M]: 0.028316846592,
        [VolumeUnit.CUBIC_INCHES]: 1728,
        [VolumeUnit.CUBIC_FEET]: 1,
      },
    };

    for (const unit of Object.values(VolumeUnit)) {
      if (!conversionTable[unit]) {
        conversionTable[unit] = {} as Record<VolumeUnit, number>;
      }
      conversionTable[unit][unit] = 1;
    }

    const factor = conversionTable[fromUnit] && conversionTable[fromUnit][toUnit];
    if (factor === undefined) {
      return 0;
    }

    return value * factor;
  }


  public static roundToFixedDigits(value: number | null, fixedDigits: number): number {
    if (!value) {
      return 0;
    }
    const factor = Math.pow(10, fixedDigits);
    return Math.round(value * factor) / factor;
  }

  public static getDimensionUnitByValue(value: string): DimensionUnit | undefined {
    for (const key in DimensionUnit) {
      if (DimensionUnit[key as keyof typeof DimensionUnit] === value) {
        return DimensionUnit[key as keyof typeof DimensionUnit];
      }
    }
    return undefined; // Return undefined if no match is found
  }

  public static getWeightUnitByValue(value: string): WeightUnit | undefined {
    for (const key in WeightUnit) {
      if (WeightUnit[key as keyof typeof WeightUnit] === value) {
        return WeightUnit[key as keyof typeof WeightUnit];
      }
    }
    return undefined; // Return undefined if no match is found
  }

  public static getVolumeUnitByValue(value: string): VolumeUnit | undefined {
    for (const key in VolumeUnit) {
      if (VolumeUnit[key as keyof typeof VolumeUnit] === value) {
        return VolumeUnit[key as keyof typeof VolumeUnit];
      }
    }
    return undefined; // Return undefined if no match is found
  }

  public static getFormattedMeasurement(value: number | null, decimals: number = 5): number | null {
    return value !== null ? this.roundToFixedDigits(value, decimals) : null;
  }

  public static setMeasurementValue(value: number | null): number | null {
    return value && isNaN(value) ? null : value;
  }
}
